import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/app/Header";
import BookOnPitchup from "../components/app/BookOnPitchup";
import { api, FACILITY_META } from "../lib/api";
import {
  ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves,
  Fish, Mountain, TrendingUp, Dog, Check, X, Star, Loader2, Share2, Copy
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves, Fish, Mountain, TrendingUp, Dog };

export default function Compare() {
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);

  const onShare = async () => {
    if (ids.length < 2) { toast.info("Pick at least 2 campsites first"); return; }
    setSharing(true);
    try {
      const { data } = await api.post("/compare/share", { ids });
      const url = `${window.location.origin}/share/${data.slug}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
      catch { toast.success("Shareable link ready below"); }
    } catch (e) { toast.error("Could not create share link"); }
    finally { setSharing(false); }
  };

  useEffect(() => {
    (async () => {
      if (!ids.length) { setLoading(false); return; }
      const { data } = await api.post("/campsites/compare", { ids });
      // keep input order
      const map = Object.fromEntries(data.map((d) => [d.campsite_id, d]));
      setSites(ids.map((i) => map[i]).filter(Boolean));
      setLoading(false);
    })();
  }, [ids]);

  const allFacs = useMemo(() => {
    const set = new Set();
    sites.forEach((s) => s.facilities.forEach((f) => set.add(f)));
    return [...set];
  }, [sites]);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="compare-page">
      <Header active="compare" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="eyebrow mb-3">02 · Side-by-Side</div>
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Compare Campsites</h1>
          {sites.length >= 2 && (
            <button onClick={onShare} disabled={sharing}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="compare-share">
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share this comparison
            </button>
          )}
        </div>

        {shareUrl && (
          <div className="mb-8 p-4 border hairline bg-[#fffdfa] flex items-center gap-3" data-testid="share-link-box">
            <div className="eyebrow shrink-0">Share URL</div>
            <code className="flex-1 mono text-xs bg-white border hairline px-2 py-1 truncate">{shareUrl}</code>
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }}
              className="btn-secondary !px-3 !py-2 text-xs flex items-center gap-1.5" data-testid="copy-share-link">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        )}

        {loading ? (
          <div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : !sites.length ? (
          <div className="p-10 border hairline bg-white text-center">
            <p className="mb-4 text-[#555]">No campsites selected.</p>
            <Link to="/browse" className="btn-primary inline-block">Browse campsites</Link>
          </div>
        ) : (
          <div className="overflow-x-auto border hairline bg-white">
            <table className="cmp-table min-w-[900px]" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="w-[180px]">Attribute</th>
                  {sites.map((s, i) => (
                    <th key={s.campsite_id} data-testid={`col-head-${i}`}>
                      <Link to={`/campsites/${s.campsite_id}`} className="font-display text-lg normal-case tracking-normal text-[#111] hover:text-[#ff4500]">{s.name}</Link>
                      <div className="text-xs text-[#555] mt-1">{s.region}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-[#fff4ee]">
                  <td className="eyebrow !text-[#ff4500]">Book now</td>
                  {sites.map((s, i) => (
                    <td key={s.campsite_id} className="!py-3">
                      <BookOnPitchup
                        campsiteId={s.campsite_id}
                        pitchupListed={s.pitchup_listed}
                        pitchupUrl={s.pitchup_url || null}
                        pitchupUrlOk={s.pitchup_url_ok}
                        campsiteName={s.name}
                        campsiteRegion={s.region}
                        websiteUrl={s.website || null}
                        phone={s.phone || null}
                        variant="primary"
                        size="sm"
                        label="Find on Pitchup"
                        testId={`compare-book-${i}`}
                        fullWidth
                        showHint
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="eyebrow">Image</td>
                  {sites.map((s) => (
                    <td key={s.campsite_id}><img src={s.image} alt={s.name} className="h-36 w-full object-cover" /></td>
                  ))}
                </tr>
                <Row label="Price / night" cells={sites.map((s) => typeof s.price_per_night === "number" ? <span><span className="text-xs text-[#888] mono">From </span><span className="mono text-lg">£{s.price_per_night.toFixed(2)}</span></span> : <span className="mono text-sm text-[#888]">See on Pitchup</span>)} />
                <Row label="Rating" cells={sites.map((s) => (
                  <span className="mono flex items-center gap-1"><Star className="w-4 h-4 fill-[#ff4500] text-[#ff4500]" /> {typeof s.rating === "number" ? s.rating.toFixed(1) : "—"}</span>
                ))} />
                <Row label="Tent" cells={sites.map((s) => boolCell(s.tent))} />
                <Row label="Caravan" cells={sites.map((s) => boolCell(s.caravan))} />
                <Row label="Campervan" cells={sites.map((s) => boolCell(s.campervan))} />
                <Row label="Trailer tent" cells={sites.map((s) => boolCell(
                  s.trailer_tent === false ? false
                    : (s.trailer_tent || (s.tent && (s.caravan || s.campervan)))
                ))} />
                <Row label="Camping pod" cells={sites.map((s) => boolCell(
                  s.camping_pod === false ? false
                    : (s.camping_pod
                       || /pod|glamp/i.test(s.name || "")
                       || /camping pod|glamping pod|wooden pod/i.test(s.description || ""))
                ))} />
                <Row label="Coordinates" cells={sites.map((s) => (
                  <span className="mono text-xs">{typeof s.lat === "number" && typeof s.lng === "number" ? `${s.lat.toFixed(3)}, ${s.lng.toFixed(3)}` : "—"}</span>
                ))} />
                {allFacs.map((f) => {
                  const meta = FACILITY_META[f]; const I = meta ? ICONS[meta.icon] : null;
                  return (
                    <tr key={f}>
                      <td className="flex items-center gap-2 eyebrow !tracking-wider">
                        {I && <I className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />} {meta?.label || f}
                      </td>
                      {sites.map((s) => <td key={s.campsite_id}>{boolCell(s.facilities.includes(f))}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, cells }) => (
  <tr>
    <td className="eyebrow">{label}</td>
    {cells.map((c, i) => <td key={i}>{c}</td>)}
  </tr>
);

const boolCell = (v) => v
  ? <Check className="w-5 h-5 text-[#ff4500]" strokeWidth={2} />
  : <X className="w-5 h-5 text-[#bbb]" strokeWidth={1.5} />;
